import { Cuepoint } from "pages/media/components/binder/types";
import { create } from "zustand";
import { produce } from "immer";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useContext } from "react";
import { ProjectContext } from "providers/ProjectProvider";

type State = {
  cuepoints: Cuepoint[];
  currentCuepoint?: Cuepoint;
  updateCount: number;
};

type Action = {
  setCuepoints: (cuepoints: State["cuepoints"]) => void;
  setUpdateCount: (updateCount: State["updateCount"]) => void;
  addCuepoint: (newCuepoint: Cuepoint) => void;
  deleteCuepoint: (highlightId: string, start: number) => void;
  deleteAllCuepoints: () => void;
  setCurrentCuepoint: (highlightId?: string) => void;
  updateCuepoint: (updatedProperties: any, revert?: boolean) => void;
  updateCuepoints: (color: string) => void;
  addUpdateCount: () => void;
  resetCuepointsStore: () => void;
};

const initialState: State = {
  cuepoints: [],
  currentCuepoint: undefined,
  updateCount: 0,
};

export const createCuepointsStore = () =>
  create<State & Action>()(
    devtools(
      immer((set) => ({
        ...initialState,
        setCuepoints: (cuepoints) => {
          set(() => ({ cuepoints }));
        },
        setUpdateCount: (updateCount) => set(() => ({ updateCount })),
        setCurrentCuepoint: (highlightId?: string) =>
          set(
            produce((state) => {
              state.currentCuepoint = state.cuepoints?.find(
                (cuepoint: Cuepoint) => {
                  return cuepoint["sourceId"] === highlightId;
                }
              );
            })
          ),
        addCuepoint: (newCuepoint) =>
          set(
            produce((state) => {
              state.cuepoints.push(newCuepoint);
              state.updateCount++;
            })
          ),
        updateCuepoint: (updatedProperties, revert?: boolean) =>
          set(
            produce((state) => {
              const cuepoint = state.cuepoints?.find(
                (cuepoint: Cuepoint) =>
                  cuepoint.highlightId === state.currentCuepoint?.highlightId &&
                  cuepoint.start === state.currentCuepoint?.start
              );

              if (cuepoint) {
                cuepoint.roughNotation = {
                  ...cuepoint.roughNotation,
                  ...updatedProperties,
                };

                if (!revert) {
                  state.updateCount++;
                }
              }
            })
          ),
        updateCuepoints: (color) =>
          set(
            produce((state) => {
              state.cuepoints.forEach((cuepoint: Cuepoint) => {
                if (cuepoint.roughNotation) {
                  cuepoint.roughNotation.color = color;
                }
              });
              if (state.currentCuepoint?.roughNotation) {
                state.currentCuepoint.roughNotation.color = color;
              }
              state.updateCount++;
            })
          ),
        deleteCuepoint: (highlightId, start) =>
          set(
            produce((state) => {
              state.cuepoints = state.cuepoints.filter((cuepoint) => {
                // legacy model support
                // let property = "highlightId";
                // if ("sourceId" in cuepoint) property = "sourceId";
                const property = "sourceId";
                return !(
                  cuepoint[property] === highlightId && cuepoint.start === start
                );
              });
              state.updateCount++;
            })
          ),
        deleteAllCuepoints: () =>
          set(
            produce((state) => {
              state.cuepoints = [];
              state.updateCount++;
            })
          ),
        addUpdateCount: () =>
          set(
            produce((state) => {
              state.updateCount++;
            })
          ),
        resetCuepointsStore: () => {
          set(initialState);
        },
      }))
    )
  );

// Hook to access cuepoints state with a selector
export const useCuepointsStore = <T = State & Action>(
  selector?: (state: State & Action) => T
): T => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useCuepointsStore must be used within ProjectProvider");
  }
  const store = context.cuepointsStore;
  return store(selector);
};
